import React from 'react';
import { View } from 'react-native';

import { shouldApplyDynamicParlevels } from '../../../common/utils/planograms/dynamicParlevels';
import { Dialog, Text } from '../../components';
import { InputCurrency, InputParlevelQuantity } from '../../components/input';
import { ProductSelector } from '../../components/selector';
import { PrimaryButton } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';

// Edit dialog for one market planogram item: product, price, quantity, parlevel and capacity.
const PointOfSaleMarketItemDialog = ({
	pointOfSale,
	planogramEditDisabled,
	isCapacityEnabled,
	editingMarketItem,
	marketItemDraft,
	isSavingMarketItemDialog,
	closeMarketItemDialog,
	updateMarketItemDraftValue,
	updateMarketItemQuantity,
	updateMarketItemParlevel,
	updateMarketItemCapacity,
	saveMarketItemChanges,
}) => {
	const styles = useThemedStyles(createStyles);

	const hasEditingMarketItemPriceBook = Boolean(editingMarketItem?.priceBook);
	const editingMarketItemPriceLocked = planogramEditDisabled || Boolean(editingMarketItem?.priceBookLocked || editingMarketItem?.priceBookApplied || hasEditingMarketItemPriceBook);
	let editingMarketItemPriceLockMessage = null;
	if (editingMarketItemPriceLocked) {
		editingMarketItemPriceLockMessage = planogramEditDisabled
			? 'Drivers cannot edit planogram prices.'
			: `Price locked by ${editingMarketItem?.priceBook?.name || 'price book'}`;
	}
	const editingMarketItemParlevelLocked = planogramEditDisabled || Boolean(editingMarketItem && shouldApplyDynamicParlevels(editingMarketItem, pointOfSale));
	let editingMarketItemParlevelLockMessage = null;
	if (editingMarketItemParlevelLocked) {
		editingMarketItemParlevelLockMessage = planogramEditDisabled
			? 'Drivers cannot edit planogram parlevels.'
			: 'Parlevel is managed by dynamic parlevels for this item.';
	}

	return (
		<Dialog
			isOpen={Boolean(editingMarketItem && marketItemDraft)}
			onClose={closeMarketItemDialog}
			enableCloseDialog={!isSavingMarketItemDialog}
		>
			{
				editingMarketItem && marketItemDraft && (
					<View style={styles.marketItemDialog}>
						<Text style={styles.marketItemDialogHeading}>Edit market item</Text>
						<Text style={styles.marketItemDialogSubheading}>{editingMarketItem.product?.name || 'Empty selection'}</Text>
						<View style={styles.marketItemDialogField}>
							<Text style={styles.marketItemDialogLabel}>Product</Text>
							<ProductSelector
								initialValue={editingMarketItem.product}
								value={marketItemDraft.product}
								onChange={updateMarketItemDraftValue('product')}
								disabled={planogramEditDisabled}
								blockDisabledProducts={true}
							/>
							<Text style={styles.marketItemDialogHint}>If a price book applies, product changes may auto-update and lock price.</Text>
						</View>
						<View style={styles.marketItemDialogRow}>
							<View style={[styles.marketItemDialogField, styles.marketItemDialogFieldHalf, styles.marketItemDialogFieldSpacing]}>
								<Text style={styles.marketItemDialogLabel}>Price</Text>
								<InputCurrency
									value={marketItemDraft.price}
									onChange={updateMarketItemDraftValue('price')}
									onChangeInternal={updateMarketItemDraftValue('price')}
									disabled={editingMarketItemPriceLocked}
								/>
								{
									editingMarketItemPriceLockMessage && (
										<Text style={styles.marketItemDialogHint}>{editingMarketItemPriceLockMessage}</Text>
									)
								}
							</View>
							<View style={[styles.marketItemDialogField, styles.marketItemDialogFieldHalf]}>
								<Text style={styles.marketItemDialogLabel}>Quantity</Text>
								<InputParlevelQuantity
									value={marketItemDraft.quantity}
									product={marketItemDraft.product}
									onChange={updateMarketItemQuantity}
									onChangeInternal={updateMarketItemQuantity}
								/>
							</View>
						</View>
						<View style={styles.marketItemDialogRow}>
							<View style={[styles.marketItemDialogField, styles.marketItemDialogFieldHalf, styles.marketItemDialogFieldSpacing]}>
								<Text style={styles.marketItemDialogLabel}>Parlevel</Text>
								<InputParlevelQuantity
									value={marketItemDraft.parlevel}
									product={marketItemDraft.product}
									onChange={updateMarketItemParlevel}
									onChangeInternal={updateMarketItemParlevel}
									disabled={editingMarketItemParlevelLocked}
								/>
								{
									editingMarketItemParlevelLockMessage && (
										<Text style={styles.marketItemDialogHint}>{editingMarketItemParlevelLockMessage}</Text>
									)
								}
							</View>
							{
								isCapacityEnabled && (
									<View style={[styles.marketItemDialogField, styles.marketItemDialogFieldHalf]}>
										<Text style={styles.marketItemDialogLabel}>Capacity</Text>
										<InputParlevelQuantity
											value={marketItemDraft.capacity}
											product={marketItemDraft.product}
											onChange={updateMarketItemCapacity}
											onChangeInternal={updateMarketItemCapacity}
											disabled={planogramEditDisabled}
										/>
									</View>
								)
							}
						</View>
						<Text style={styles.marketItemDialogHint}>Mobile changes apply immediately. Product/price edits clear queued values for this item.</Text>
						<PrimaryButton
							style={styles.marketItemDialogSaveButton}
							label="Save changes"
							loading={isSavingMarketItemDialog}
							onPress={() => {
								void saveMarketItemChanges();
							}}
						/>
					</View>
				)
			}
		</Dialog>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	marketItemDialog: {
		paddingTop: space.sm,
	},
	marketItemDialogHeading: {
		...type.cardTitle,
		color: colors.text,
		marginBottom: space.xs,
	},
	marketItemDialogSubheading: {
		...type.secondary,
		color: colors.textSecondary,
		marginBottom: space.md,
	},
	marketItemDialogRow: {
		flexDirection: 'row',
	},
	marketItemDialogField: {
		marginBottom: space.md,
	},
	marketItemDialogFieldHalf: {
		flex: 1,
	},
	marketItemDialogFieldSpacing: {
		marginRight: space.sm,
	},
	marketItemDialogLabel: {
		...type.label,
		color: colors.textSecondary,
		marginBottom: space.xs,
	},
	marketItemDialogHint: {
		...type.caption,
		marginTop: space.xs,
		color: colors.textSecondary,
	},
	marketItemDialogSaveButton: {
		marginTop: space.sm,
	},
});

export default PointOfSaleMarketItemDialog;
